import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useWatch } from 'react-hook-form';

// Styles
import styles from './FilterConditionalFilters.module.css';

// Components
import Button from '../../atoms/Button/Button';
import ConditionalFiltersRender from './ConditionalFiltersRender';
import ConditionalFiltersList from '../../molecules/ConditionalFiltersList/ConditionalFiltersList';
import ConditionalFiltersItems, { ConditionalFiltersItem } from '../../molecules/ConditionalFiltersItem/ConditionalFiltersItem';
import SelectField from '../../atoms/Select/Select';

/**
 * FilterConditionalFilters
 * @param {object} props
 * @param {ReturnType<typeof import('../../../domain/tableConditionalFilter/useConditionalFilters').default>} props.conditionalFilters - The conditional filters hook return
 * @param {string} [props.className]
 * @param {(data: Array<ApiManagerFilterValues>) => void} props.onSubmit
 * @param {boolean} props.isLoading
 * @param {boolean} props.isSubmitting
 * @returns {JSX.Element}
 */
function FilterConditionalFilters({
  conditionalFilters,
  className = '',
  onSubmit,
  isLoading,
  isSubmitting,
}) {
  const { t } = useTranslation();

  const {
    tableFiltersGrouped,
    conditionalFiltersById,
    formConditionalFilters: {
      control: conditionalFiltersControl,
      handleSubmit: handleSubmitConditionalFilters,
    },
    conditionalFiltersFields,
    handleAddConditionalFilter,
    handleRemoveConditionalFilter,
    handleUpdateConditionalFilterType,
    handleUpdateConditionalFilterOperator,
    handleClearAllConditionalFilters,
    handleCancelConditionalFilters,
    criterionSelectFocusIndex,
    clearCriterionSelectFocus,
  } = conditionalFilters;

  const criterionSelectRefs = useRef(/** @type {Record<number, { focus: () => void } | null>} */ ({}));

  useEffect(() => {
    if (criterionSelectFocusIndex === null) return;

    const selectRef = criterionSelectRefs.current[criterionSelectFocusIndex];
    if (selectRef?.focus) {
      selectRef.focus();
      clearCriterionSelectFocus();
    }
  }, [
    criterionSelectFocusIndex,
    conditionalFiltersFields.length,
    clearCriterionSelectFocus,
  ]);

  // Watch all conditional filters to get selected IDs
  const watchedAllFilters = useWatch({
    control: conditionalFiltersControl,
    name: 'conditionalFilters',
  });

  return (
    <form
      id="conditional-filters-form"
      className={`${styles.container} ${className}`}
      onSubmit={handleSubmitConditionalFilters((
        /** @type {{ conditionalFilters: ApiManagerGlobalFiltersCreateValue[] }} */ data,
      ) => {
        const validFilters = data.conditionalFilters.filter(
          (/** @type {ApiManagerGlobalFiltersCreateValue} */ filter) => filter.id && filter.operator,
        );
        onSubmit(/** @type {Array<ApiManagerFilterValues>} */ (validFilters));
      })}
    >
      {/* Header */}
      <div className={styles.header}>
        <div className={styles['header-title']}>
          <h3 className={styles['header-title']}>
            {t('table.filters.others')}
          </h3>
        </div>
        <div className={styles['header-actions']}>
          <Button
            color="no-background"
            icon="refresh"
            size="small"
            iconPosition="right"
            onClick={() => handleClearAllConditionalFilters()}
          >
            {t('table.filters.clearAll')}
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className={styles.content}>
        <div className={styles['content-body']}>
          <ConditionalFiltersList fields={conditionalFiltersFields}>
            {(field, index) => (
              <ConditionalFiltersItems
                onRemove={() => handleRemoveConditionalFilter(index)}
                required={conditionalFiltersById[field.id]?.required}
              >
                <ConditionalFiltersItem>
                  <Controller
                    name={`conditionalFilters.${index}.id`}
                    control={conditionalFiltersControl}
                    render={({
                      field: { ref, ...fieldInput },
                      fieldState: { error },
                    }) => {
                      // Combine with isOptionDisabled to disabled options in the select
                      const selectedIds = watchedAllFilters
                        ?.map((/** @type {ApiManagerGlobalFiltersCreateValue} */ filter, idx) => (
                          idx !== index && filter?.id ? filter.id : null
                        )).filter(Boolean) || [];

                      const optionsGrouped = tableFiltersGrouped?.map((group) => ({
                        label: group.lib,
                        options: group.options.map((filter) => ({
                          value: filter.id,
                          label: filter.lib,
                          color: filter.type === 'select' || filter.type === 'select_multiple'
                            ? filter.color
                            : undefined,
                        })),
                      })) || [];

                      return (
                        <SelectField
                          ref={(node) => {
                            criterionSelectRefs.current[index] = node;
                          }}
                          id={`conditionalFilters.${index}.id`}
                          value={fieldInput.value}
                          onChange={(
                            /** @type {{ label: string, value: string }} */ value,
                          ) => {
                            handleUpdateConditionalFilterType(index, {
                              ...field,
                              id: value?.value,
                            });
                            fieldInput.onChange(value.value);
                          }}
                          options={optionsGrouped}
                          isOptionDisabled={(option) => {
                            // Always allow the current value
                            if (option.value === fieldInput.value) return false;
                            return selectedIds.includes(option.value);
                          }}
                          placeholder={t('table.filters.selectCriterion')}
                          error={t(error?.message || '')}
                          disabled={conditionalFiltersById[field.id]?.required}
                        />
                      );
                    }}
                  />
                </ConditionalFiltersItem>

                <ConditionalFiltersItem hasValue={!!field.id}>
                  <Controller
                    name={`conditionalFilters.${index}.operator`}
                    control={conditionalFiltersControl}
                    render={({
                      field: { ref, ...fieldInput },
                      fieldState: { error },
                    }) => (
                      <SelectField
                        id={`conditionalFilters.${index}.operator`}
                        value={fieldInput.value}
                        onChange={(
                          /** @type {{ label: string, value: ApiManagerGlobalFiltersOperator }} */ value,
                        ) => {
                          handleUpdateConditionalFilterOperator(index, {
                            ...field,
                            operator: value?.value,
                          });
                          fieldInput.onChange(value.value);
                        }}
                        options={conditionalFiltersById[field.id]?.operatorOptions.map((
                          /** @type {ApiManagerGlobalFiltersOperator} */ operator,
                        ) => ({
                          value: operator,
                          label: t(`conditionalFilters.operators.${operator}`),
                        }))}
                        placeholder={t('conditionalFilters.operator.select')}
                        disabled={!field.id}
                        error={t(error?.message || '')}
                      />
                    )}
                  />
                </ConditionalFiltersItem>

                <ConditionalFiltersItem
                  hasValue={!!field.id}
                  // Used to add between filter when api ready, actually not supported
                  // isFlex={field.operator === 'between'}
                >
                  <ConditionalFiltersRender
                    controlRhf={conditionalFiltersControl}
                    field={field}
                    name={`conditionalFilters.${index}.values`}
                    filtersById={conditionalFiltersById}
                  />
                </ConditionalFiltersItem>
              </ConditionalFiltersItems>
            )}
          </ConditionalFiltersList>
        </div>
        <div className={styles['content-actions']}>
          <Button
            color="no-background-black"
            icon="add"
            size="small"
            iconPosition="right"
            onClick={() => handleAddConditionalFilter()}
          >
            {t('table.filters.addFilter')}
          </Button>
        </div>
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <div className={styles['footer-actions']}>
          <Button
            color="no-background"
            size="small"
            onClick={handleCancelConditionalFilters}
          >
            {t('table.filters.cancel')}
          </Button>
          <Button
            size="small"
            isSubmit
            isLoading={isLoading || isSubmitting}
          >
            {t('table.filters.apply')}
          </Button>
        </div>
      </div>
    </form>
  );
}

export default FilterConditionalFilters;
